/**
 * The Argus section: six rooms. Inbox, Rules, Backtest and History hold
 * something; Health and Discoveries are empty and say so, not pretending.
 *
 * Phase A0 of `docs/plans/argus.md` builds the shape of the product and stores nothing.
 * Every room says what will live in it and which phase brings it, because the
 * alternative - an example list, a placeholder chart - teaches people to read a
 * screen that is lying to them, and Argus is a system whose whole worth is that
 * its screens can be believed.
 *
 * The room tabs are drawn by `app-root` in the header's second row, the way
 * Explore and the Assistant draw theirs. This component renders only what is
 * under them.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { activeSection, canDo, isRootUser, myAccess, myPermissions } from '../../state/store.ts';
import { activeLens } from '../../state/lens.ts';
import { ARGUS_ROOMS, argusEpisode, argusRoom, type ArgusRoomInfo } from './argus-lens.ts';
// Registers the overlay provider. Imported here because this is the module the
// shell already pulls in for Argus - the provider needs no separate wiring in
// `app-root`, which is the point: Argus reaches charts through the registry.
import './argus-overlays.ts';
import './argus-rules-room.ts';
import './argus-inbox-room.ts';
import './argus-history-room.ts';
import './argus-backtest-room.ts';
import './argus-health-room.ts';
import './argus-episode-page.ts';
import { startAttention } from './argus-inbox-store.ts';
// The Assistant's rule-draft card, registered through the chat's card seam.
import './argus-assistant-card.ts';

// The count beside ARGUS. It asks nothing until the account is known to have
// Argus and is looking through it (see the store).
startAttention();

@customElement('argus-section')
export class ArgusSection extends LightElement {
  private store = new StoreController(this, [activeSection, argusRoom, argusEpisode, activeLens, myPermissions, myAccess, isRootUser]);

  private room(): ArgusRoomInfo {
    // `ARGUS_ROOMS` is never empty, but the array index does not say so - and
    // the Inbox is the right answer for an id that has gone away anyway.
    const fallback = ARGUS_ROOMS[0] as ArgusRoomInfo;
    return ARGUS_ROOMS.find((r) => r.id === argusRoom.value) ?? fallback;
  }

  private renderRoom(room: ArgusRoomInfo): TemplateResult {
    // The first room with something in it. The rest still say what they are
    // waiting for, which is the honest thing to show until they hold anything.
    // One episode's triage page, inside the room it was opened from.
    if ((room.id === 'inbox' || room.id === 'history') && argusEpisode.value !== null) {
      return html`<argus-episode-page .episodeId=${argusEpisode.value}></argus-episode-page>`;
    }
    if (room.id === 'rules') return html`<argus-rules-room></argus-rules-room>`;
    if (room.id === 'inbox') return html`<argus-inbox-room></argus-inbox-room>`;
    if (room.id === 'history') return html`<argus-history-room></argus-history-room>`;
    if (room.id === 'backtest') return html`<argus-backtest-room></argus-backtest-room>`;
    if (room.id === 'health') {
      if (canDo('argus:health')) return html`<argus-health-room></argus-health-room>`;
      return html`<div class="argus-room"><h2>Health</h2>
        <p class="argus-room-blurb">The Health room is for admins only, for now.</p></div>`;
    }
    return html`
      <div class="argus-room">
        <h2>${room.label}</h2>
        <p class="argus-room-blurb">${room.blurb}</p>
        <p class="argus-room-waiting">
          <span class="argus-room-waiting-label">Not built yet</span>
          ${room.waitingFor}
        </p>
      </div>
    `;
  }

  /**
   * Said once, at the top, rather than discovered by a refused click: an
   * account let in by an Argus Viewer role or by a Features audience reads
   * everything and changes nothing.
   */
  private renderReadOnly(): TemplateResult | typeof nothing {
    if (canDo('argus:triage') || canDo('argus:author')) return nothing;
    return html`<p class="argus-lfaint argus-readonly" role="note">
      <b>Read only.</b> You can look at rules, alerts and history. Triage needs the Argus Operator
      role; writing rules needs Argus Rule Manager.
    </p>`;
  }

  override render(): TemplateResult {
    return html`
      <div class="argus is-${this.room().id} ${argusEpisode.value !== null ? 'has-episode' : ''}">
        ${this.renderReadOnly()}
        ${this.renderRoom(this.room())}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-section': ArgusSection;
  }
}
